import { geojson as fgb } from "flatgeobuf";
import { normalizeId } from "./helpers";

const GEO_URLS = {
  electorate: "/NZ_maps/newZealandConstituency.fgb",
  SA3:        "/NZ_maps/newZealandSA3.fgb",
  SA2:        "/NZ_maps/newZealandSA2.fgb",
};

const NZ_BOUNDS = [[166.0, -47.5], [179.0, -33.0]];

// LRU cache keyed by level
const _geoCache = new Map();
const _geoCacheMaxSize = 6;
const _geoInflight = new Map();

export async function loadGeo({
  level,
  doFit = true,
  backgroundOnly = false,
  updateAreas = true,
  setState,
  mapRef,
  handlePossibleAreasChange,
}) {
  const cached = _geoCache.get(level);

  if (cached) {
    if (backgroundOnly) return;
    if (updateAreas && typeof handlePossibleAreasChange === "function") {
      handlePossibleAreasChange(cached.areas);
    }
    setState({ geoData: cached.geo, styledGeo: null }, () => {
      if (!doFit) return;
      const map = mapRef?.current?.getMap?.();
      if (map) map.fitBounds(NZ_BOUNDS, { padding: 20, duration: 500 });
    });
    return;
  }

  const url = process.env.PUBLIC_URL + (GEO_URLS[level] || "");
  if (!url) { console.error(`[loadGeo] Unknown level: ${level}`); return; }

  try {
    let rawFeatures;
    if (_geoInflight.has(level)) {
      rawFeatures = await _geoInflight.get(level);
    } else {
      const fetchPromise = (async () => {
        const response = await fetch(url);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const features = [];
        for await (const feature of fgb.deserialize(response.body)) features.push(feature);
        return features;
      })();
      _geoInflight.set(level, fetchPromise);
      try { rawFeatures = await fetchPromise; }
      finally { _geoInflight.delete(level); }
    }

    const geo = {
      type: "FeatureCollection",
      features: rawFeatures.map((f) => {
        const id = normalizeId(level, f.properties);
        return {
          ...f,
          properties: {
            ...f.properties,
            area_cd: id,
            area_id: id != null ? Number(id) : null,
          },
        };
      }),
    };

    const possibleAreas = geo.features
      .filter((f) => f.properties.area_name || f.properties.area_id)
      .map((f) => ({
        area_name: f.properties.area_name || String(f.properties.area_id),
        area_id: f.properties.area_id,
        level,
      }))
      .sort((a, b) => String(a.area_name).localeCompare(String(b.area_name)));

    if (_geoCache.size >= _geoCacheMaxSize) {
      _geoCache.delete(_geoCache.keys().next().value);
    }
    _geoCache.set(level, { geo, areas: possibleAreas });

    if (updateAreas && typeof handlePossibleAreasChange === "function") {
      handlePossibleAreasChange(possibleAreas);
    }

    if (backgroundOnly) return;

    setState({ geoData: geo, styledGeo: null }, () => {
      if (!doFit) return;
      const map = mapRef?.current?.getMap?.();
      if (map) map.fitBounds(NZ_BOUNDS, { padding: 20, duration: 500 });
    });
  } catch (error) {
    console.error("[loadGeo] Shape load failed:", error);
  }
}
